import {
    AnalyticsMembershipEvent,
    BackendMembershipEvent,
} from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import {
    trackForMembership,
    trackGrowthMarketingEvent,
} from '../../logger/Logger';
import { useUpdateMemberJourneyLog } from '../../member-profile/hooks/useUpdateMemberJourneyLog';
import useUpdateMembershipScheduledDiscoveryCall from '../../onboarding/hooks/useUpdateMembershipScheduledDiscoveryCall';
import { useUser } from '../../user/providers/UserProvider';
import { GuidanceSessionType } from '../types/GuidanceSessions';

export const useAfterBookingGuidanceSession = () => {
    const user = useAuthedUser();
    const { setOptimisticUserData } = useUser();
    const queryClient = useQueryClient();
    const { updateMemberJourneyLog } = useUpdateMemberJourneyLog();
    const { mutateAsync: updateMembershipScheduledDiscoveryCall } =
        useUpdateMembershipScheduledDiscoveryCall();

    const afterBookingGuidanceSession = useCallback(
        async (
            guidanceSessionType: GuidanceSessionType,
            isFirstKickoffSession?: boolean,
        ) => {
            trackForMembership(
                AnalyticsMembershipEvent.GUIDANCE_SESSION_SCHEDULED_VIA_CALENDLY,
                {
                    guidanceSessionType,
                },
            );

            const membershipId = user.membership?.id ?? '';
            if (isFirstKickoffSession === true) {
                const eventName =
                    BackendMembershipEvent.FIRST_KICKOFF_SESSION_BOOKED;
                trackGrowthMarketingEvent(eventName, {
                    eventId: `${eventName}:${membershipId}:${user.id}`,
                    currency: 'USD',
                    value: 0,
                });
            }

            if (guidanceSessionType === GuidanceSessionType.DISCOVERY) {
                // await this call so its change will be included in the user refetch handled by updateMemberJourneyLog
                // TODO remove this once all active client versions are referencing memberJourneyLog instead of membership.hasScheduledDiscoveryCall
                await updateMembershipScheduledDiscoveryCall({
                    urlParams: { userId: user.id },
                });
                void updateMemberJourneyLog({
                    bookedDiscoverySession: true,
                });
            } else if (guidanceSessionType === GuidanceSessionType.ROADMAP) {
                void updateMemberJourneyLog({
                    bookedRoadmapSession: true,
                });
            }

            await queryClient.refetchQueries({
                queryKey: ['searchGuidanceSessions'],
                type: 'all',
            });
        },
        [
            updateMemberJourneyLog,
            updateMembershipScheduledDiscoveryCall,
            queryClient,
            setOptimisticUserData,
        ],
    );

    return { afterBookingGuidanceSession };
};
